import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "billingCycle", "planSelection", "displayPrice", "creditsDisplay",
    "totalDisplay", "periodLabel", "planSelectionTotal", "planNameSummary",
    "billingPeriodSummary", "planDetailsContainer", "planDetailsContent",
    "billingHeaderTotal", "billingPaymentTotal", "planDetailsCredits",
    "planDetailsSearches", "planDetailsVerifications"
  ]

  static values = {
    monthlyPrices: Object,
    yearlyPrices: Object,
    planFeatures: Object,
    selectedLevel: Number,
    selectedBilling: { type: String, default: 'yearly' }
  }

  connect() {
    this.setupCheckoutListener()
    this.updatePrices()
  }

  setupCheckoutListener() {
    window.addEventListener('checkoutControllerConnected', (event) => {
      const controller = event.detail.controller
      if (controller?.checkButtonState) {
        setTimeout(() => controller.checkButtonState(), 100)
      }
    })
  }

  updatePrices() {
    const billingCycle = this.getSelectedBillingCycle()
    const level = this.getSelectedPlanLevel()

    this.selectedLevelValue = level
    this.selectedBillingValue = billingCycle

    this.updateCheckoutFormFields(level, billingCycle)
    this.updatePriceDisplay(billingCycle, level)
    this.updatePlanDetailsFeatures(billingCycle)
    this.updateVisiblePlanDetails()
  }

  updatePriceDisplay(billingCycle, level) {
    if (billingCycle === "yearly") {
      this.updateForYearly(level)
    } else {
      this.updateForMonthly(level)
    }
  }

  togglePlanDetails(event) {
    event.preventDefault()
    event.stopPropagation()

    const level = parseInt(event.currentTarget.dataset.level)

    // Hide all plan details content
    this.planDetailsContentTargets.forEach(el => el.classList.add("d-none"))

    // Show the details for the clicked plan
    const detailsContent = this.planDetailsContentTargets.find(
      el => parseInt(el.dataset.level) === level
    )

    if (detailsContent) {
      this.planDetailsContainerTarget.classList.remove("d-none")
      detailsContent.classList.remove("d-none")
      this.updatePlanDetailsFeatures(this.selectedBillingValue)
    }
  }

  closePlanDetails() {
    this.planDetailsContainerTarget.classList.add("d-none")
  }

  updateForYearly(level) {
    this.updateDisplayPrices("yearly")

    this.periodLabelTargets.forEach(el => {
      el.textContent = i18n.t("js.back.checkout.pricing.per_year")
    })

    const yearlyTotal = this.yearlyPricesValue[level]?.total || ""
    const yearlyTotalWithPeriod = yearlyTotal + "/" + i18n.t("js.back.checkout.pricing.year")

    this.planSelectionTotalTargets.forEach(el => el.textContent = yearlyTotal)
    this.billingHeaderTotalTargets.forEach(el => el.textContent = yearlyTotalWithPeriod)
    this.billingPaymentTotalTargets.forEach(el => el.textContent = yearlyTotal)

    this.updateSummaryLabels(level, "yearly")
  }

  updateForMonthly(level) {
    this.updateDisplayPrices("monthly")

    this.periodLabelTargets.forEach(el => {
      el.textContent = i18n.t("js.back.checkout.pricing.per_month")
    })

    const monthlyPrice = this.monthlyPricesValue[level]?.price || ""
    const monthlyPriceWithPeriod = monthlyPrice + "/" + i18n.t("js.back.checkout.pricing.month")

    this.planSelectionTotalTargets.forEach(el => el.textContent = monthlyPrice)
    this.billingHeaderTotalTargets.forEach(el => el.textContent = monthlyPriceWithPeriod)
    this.billingPaymentTotalTargets.forEach(el => el.textContent = monthlyPrice)

    this.updateSummaryLabels(level, "monthly")
  }

  updateDisplayPrices(billingCycle) {
    const pricesValue = billingCycle === "yearly" ? this.yearlyPricesValue : this.monthlyPricesValue

    this.displayPriceTargets.forEach(el => {
      const level = parseInt(el.dataset.level)
      el.textContent = pricesValue[level]?.price || ""
    })

    this.totalDisplayTargets.forEach(el => {
      const level = parseInt(el.dataset.level)
      el.textContent = pricesValue[level]?.total || ""
    })

    this.creditsDisplayTargets.forEach(el => {
      const level = parseInt(el.dataset.level)
      el.textContent = pricesValue[level]?.credits || ""
    })
  }

  updatePlanDetailsFeatures(billingCycle) {
    if (!this.planFeaturesValue || Object.keys(this.planFeaturesValue).length === 0) {
      return
    }

    // Update credits in plan details
    this.planDetailsCreditsTargets.forEach(el => {
      const level = parseInt(el.dataset.level)
      const features = this.planFeaturesValue[level]?.[billingCycle]
      if (features?.credits) {
        el.textContent = i18n.t("js.back.checkout.pricing.features.credits", { count: features.credits })
      }
    })

    // Update searches in plan details
    this.planDetailsSearchesTargets.forEach(el => {
      const level = parseInt(el.dataset.level)
      const features = this.planFeaturesValue[level]?.[billingCycle]
      if (features?.credits?.searches) {
        el.textContent = i18n.t("js.back.checkout.pricing.features.searches", { count: features.credits.searches })
      }
    })

    // Update verifications in plan details
    this.planDetailsVerificationsTargets.forEach(el => {
      const level = parseInt(el.dataset.level)
      const features = this.planFeaturesValue[level]?.[billingCycle]
      if (features?.credits?.verifications) {
        el.textContent = i18n.t("js.back.checkout.pricing.features.verifications", { count: features.credits.verifications })
      }
    })
  }

  updateSummaryLabels(level, billingCycle) {
    const pricesValue = billingCycle === "yearly" ? this.yearlyPricesValue : this.monthlyPricesValue

    if (this.hasPlanNameSummaryTarget) {
      this.planNameSummaryTarget.textContent = pricesValue[level]?.name || ""
    }

    if (this.hasBillingPeriodSummaryTarget) {
      const key = billingCycle === "yearly" ? "yearly_billing" : "monthly_billing"
      this.billingPeriodSummaryTarget.textContent = i18n.t(`js.back.checkout.pricing.${key}`)
    }
  }

  updateVisiblePlanDetails() {
    const isDetailsVisible = this.hasPlanDetailsContainerTarget &&
      !this.planDetailsContainerTarget.classList.contains("d-none")

    if (isDetailsVisible) {
      const visibleDetails = this.planDetailsContentTargets.find(
        el => !el.classList.contains("d-none")
      )

      if (visibleDetails) {
        const currentLevel = parseInt(visibleDetails.dataset.level)
        const selectedLevel = this.selectedLevelValue

        if (currentLevel !== selectedLevel) {
          visibleDetails.classList.add("d-none")

          const newDetails = this.planDetailsContentTargets.find(
            el => parseInt(el.dataset.level) === selectedLevel
          )

          if (newDetails) {
            newDetails.classList.remove("d-none")
          }
        }
      }
    }
  }

  updateCheckoutFormFields(level, billingCycle) {
    // Always update form fields directly (most reliable)
    this.updateFormFieldsDirectly(level, billingCycle)

    // Try to update via controller if available
    const checkoutController = this.application.getControllerForElementAndIdentifier(
      this.element,
      "checkout"
    )

    if (checkoutController) {
      if (checkoutController.hasLevelTarget) {
        checkoutController.levelTarget.value = level
      }
      if (checkoutController.hasBillingCycleTarget) {
        checkoutController.billingCycleTarget.value = billingCycle
      }
      if (checkoutController.checkButtonState) {
        checkoutController.checkButtonState()
      }
    }
  }

  updateFormFieldsDirectly(level, billingCycle) {
    const levelField = document.querySelector('[data-checkout-target="level"]')
    const billingField = document.querySelector('[data-checkout-target="billingCycle"]')

    if (levelField) levelField.value = level
    if (billingField) billingField.value = billingCycle
  }

  selectPlan(level) {
    const planRadio = this.planSelectionTargets.find(
      el => parseInt(el.value) === level
    )

    if (planRadio && !planRadio.checked) {
      planRadio.checked = true
      this.updatePrices()
    }
  }

  getSelectedBillingCycle() {
    const selectedRadio = this.billingCycleTargets.find(el => el.checked)
    return selectedRadio ? selectedRadio.value : this.selectedBillingValue
  }

  getSelectedPlanLevel() {
    const selectedRadio = this.planSelectionTargets.find(el => el.checked)
    return selectedRadio ? parseInt(selectedRadio.value) : this.selectedLevelValue
  }

  switchToBillingCycle(billingCycle) {
    this.selectedBillingValue = billingCycle
    this.updateCheckoutFormFields(this.selectedLevelValue, billingCycle)
    this.updatePrices()
  }
}
